import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { API, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { SignaturePad } from "@/components/SignaturePad";
import { Brand } from "@/components/Brand";
import { toast } from "sonner";
import { CheckCircle2, FileText, Loader2 } from "lucide-react";

export default function SignAgreement() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [agree, setAgree] = useState(false);
  const [name, setName] = useState("");
  const [sig, setSig] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/sign/${token}`).then((r) => setInfo(r.data)).catch((e) => setErr(apiError(e))); }, [token]);
  const submit = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/sign/${token}`, { agree, name, signature_image: sig }); setInfo({ ...info, ...data }); toast.success("Signed - thank you!"); }
    catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  const fileUrl = `${API}/sign/${token}/file`;
  return (
    <div className="min-h-screen bg-[#37494d] px-4 py-10">
      <div className="mx-auto max-w-3xl rounded-2xl bg-white p-6 shadow-2xl sm:p-8" data-testid="sign-page">
        <div className="mb-6"><Brand /></div>
        {err ? <p className="text-sm text-rose-600" data-testid="sign-error">{err}</p> : !info ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" /> : (
          <>
            <h1 className="font-display text-2xl font-extrabold text-slate-900">Buyer-brokerage agreement</h1>
            <p className="mb-4 text-sm text-slate-600">{info.realtor_name} sent this agreement to {info.buyer_name}. Please review it, then sign below.</p>
            <a href={fileUrl} target="_blank" rel="noreferrer" className="mb-3 inline-flex items-center text-sm font-semibold text-amber-700 underline" data-testid="sign-open-file"><FileText className="mr-1 h-4 w-4" />Open {info.filename}</a>
            {info.content_type === "application/pdf"
              ? <iframe title="Agreement" src={fileUrl} className="mb-6 h-[60vh] w-full rounded-lg border border-slate-200" data-testid="sign-pdf" />
              : <img src={fileUrl} alt="Agreement" className="mb-6 w-full rounded-lg border border-slate-200" />}
            {info.signed_at ? (
              <div className="flex items-center gap-2 rounded-lg bg-emerald-50 p-4 text-sm font-semibold text-emerald-800" data-testid="sign-done">
                <CheckCircle2 className="h-5 w-5" />Signed by {info.signed_name} on {new Date(info.signed_at).toLocaleString()}</div>
            ) : (
              <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
                <label className="flex items-start gap-2 text-sm text-slate-800">
                  <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} className="mt-0.5" data-testid="sign-agree" />
                  I have read this agreement and agree to sign it electronically. My electronic signature is as binding as a handwritten one.
                </label>
                <div><Label>Type your full legal name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 bg-white font-serif italic" data-testid="sign-name" /></div>
                <div><Label>Signature</Label><div className="mt-1"><SignaturePad onChange={setSig} /></div></div>
                <Button onClick={submit} disabled={busy || !agree || name.trim().length < 3 || !sig} className="gold-cta w-full border-0 font-bold" data-testid="sign-submit">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign agreement"}</Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
